import * as THREE from 'three';
import type { Sprite } from '../../ui/data/spriteTypes';
import type { FishPose } from '../fishSchools';
import { hash3 } from '../fishSwim';
import { inView } from '../persistence';
import type { ReefHuntRate } from './data';

/**
 * The Reef's hunts (M251, the user's brief, `docs/HUNTS_BRIEF.md`): now and then, where the player is watching, a
 * predator drawn on the Reef strikes at a prey fish drawn there, and catches it or misses.
 *
 * Only what the simulation holds is drawn (the brief's section 4, "The existing simulation must remain authoritative").
 * Each day the scene is given each predator's expected catch of each of its prey among the fish it draws
 * (`ReefHuntRate`, `reefHuntsIn`: the day's own predation, no draw taken). The day's catches are that expectation
 * rounded by a draw of the day (`dayAttempts`), and its misses the catches' share of the strikes that miss
 * (`CAPTURE_SUCCESS`). They are only drawn, never counted: the simulation's deaths are its own, `stepReef`'s, already
 * in the next day's counts. So a fish caught here is hidden only until the scene's next day, when the counts the
 * simulation gives (its deaths among them) are drawn again; and a day's strikes not seen (none in view, or the day gone
 * by at a quick speed) are let go with it, never saved up (the brief's "Avoid playing hundreds of hunting animations
 * when simulation time is accelerated"). A predator or a prey the simulation does not set together never meets here:
 * the pairs are the day's rates', each of the group of the prey's fish the simulation takes (its young, its adults or its
 * terminal males, `ReefHuntRate.group`, M252), so a strike falls on a drawn fish of that group: the sizes are the
 * simulation's own (`PREY_SIZE`'s 5 to 20 cm, the moano kea at any length), a drawn fish standing for its group's fish
 * at their mean length (the redlip's young, 10 to 20 cm among those taken, drawn at their 208 mm mean).
 *
 * Each strike runs on the animation clock (the player's own seconds, as the fish swim, on while the game is paused,
 * M95), in the brief's stages (`HuntState`), each predator by its profile (`HUNT_PROFILES`), the fish's own swimming
 * underneath: a hunt only poses them (`FishPose`), drawn its blend of the way from where they swim, and gives them back
 * by easing that blend to none.
 */

/** A hunt's stage (the brief's section 9): a pursuer turning round and following its prey (M258), the predator turning to its prey, gathering, striking, catching, recovering; or the prey getting away. */
export type HuntState = 'idle' | 'follow' | 'targeting' | 'anticipation' | 'lunge' | 'capture' | 'recovery' | 'escape';

/** The ways a predator strikes (the brief's section 2): from cover, in pursuit, a shark's approach, along the bottom. */
export type HuntProfileId = 'ambush' | 'pursuit' | 'approach' | 'benthic';

/**
 * How a predator strikes (the brief's section 2, E), each stage's span in seconds a range a strike takes its own draw
 * in, and its distances in the predator's own lengths. The roi's and the jacks' spans are the brief's own (its
 * "Suggested timing", its section 1's ranges where it gives one span), the shark's and the octopus's JUDGED within its
 * section 1's (the brief gives them no timing); the distances, the reach and the easing JUDGED to read on screen as a
 * strike of that kind, kept restrained (the brief's "Keep this extremely restrained"). The jacks and the shark follow a
 * prey behind them or past their reach before they strike (M258, the user's word: "what if the predator turns around and
 * then follows the prey for a little before attacking?"), from half again their reach (`followBL`, JUDGED: near enough
 * to read as one prey chosen, not a fish across the scene): they hunt by pursuit in the open (the brief's B and C).
 */
export interface HuntProfile {
  id: HuntProfileId;
  /** Turning to its prey and closing on it slowed: seconds. */
  targetingS: readonly [number, number];
  /** Gathering before the strike: seconds (none for a fish in pursuit). */
  anticipationS: readonly [number, number];
  /** Drawn back before it strikes, its lengths (the brief's 3 to 8%; none for a fish in pursuit). */
  backBL: readonly [number, number];
  /** Its width drawn this much less as it gathers (the brief's 2 to 4%), or more where below 0 (an octopus reaching). */
  squash: readonly [number, number];
  /** The strike: seconds. */
  lungeS: readonly [number, number];
  /** How far its mouth may start from its prey, its lengths: nearer, a strike; farther, none. */
  reachBL: number;
  /** The least, its lengths: a prey already at its mouth is not struck at. */
  nearBL: number;
  /** Its speed as it closes on its prey in targeting, a share of the strike's (the shark's deliberate approach the most). */
  closeShare: number;
  /** The catch: seconds. */
  captureS: readonly [number, number];
  /** Back to its own swimming: seconds. */
  recoveryS: readonly [number, number];
  /** On past its prey on a miss, its lengths; and on past the catch where `followThrough`. */
  overshootBL: readonly [number, number];
  /** Whether it carries on past a catch (the shark's "slight continuation past the capture location"). */
  followThrough: boolean;
  /** Its strike's easing: the share of its way done at a share of its time (rapid at first, eased at the end). */
  ease: (t: number) => number;
  /**
   * How far its mouth may start from a prey it follows before it strikes, its lengths (M258): a prey behind it, or ahead
   * past its reach, turned to and followed; 0 for a predator that strikes only what is ahead of it in reach (M257).
   */
  followBL: number;
}

const easeOut = (power: number) => (t: number): number => 1 - (1 - Math.min(1, Math.max(0, t))) ** power;
const smooth = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

export const HUNT_PROFILES: Readonly<Record<HuntProfileId, HuntProfile>> = {
  // The roi's (the brief's A, "the most sudden and explosive"): its own timings, a short burst from where it holds.
  ambush: { id: 'ambush', targetingS: [0.15, 0.25], anticipationS: [0.13, 0.17], backBL: [0.03, 0.08], squash: [0.02, 0.04], lungeS: [0.12, 0.18], reachBL: 2.2, nearBL: 0.3, closeShare: 0.05, captureS: [0.09, 0.11], recoveryS: [0.22, 0.28], overshootBL: [0.15, 0.3], followThrough: false, ease: easeOut(3), followBL: 0 },
  // The jacks' (the brief's B): no windup, a longer chase, caught without stopping.
  pursuit: { id: 'pursuit', targetingS: [0.15, 0.17], anticipationS: [0, 0], backBL: [0, 0], squash: [0, 0], lungeS: [0.25, 0.5], reachBL: 3.5, nearBL: 0.5, closeShare: 0.25, captureS: [0.1, 0.11], recoveryS: [0.2, 0.24], overshootBL: [0.4, 0.8], followThrough: false, ease: easeOut(2), followBL: 5.25 },
  // A shark's (the brief's C): a deliberate approach, a strong last acceleration, carried on past the catch.
  approach: { id: 'approach', targetingS: [0.3, 0.4], anticipationS: [0, 0], backBL: [0, 0], squash: [0, 0], lungeS: [0.2, 0.25], reachBL: 1.6, nearBL: 0.2, closeShare: 0.4, captureS: [0.11, 0.13], recoveryS: [0.35, 0.42], overshootBL: [0.25, 0.45], followThrough: true, ease: smooth, followBL: 2.4 },
  // An octopus's (the brief's D): along the reef, a short reach, the prey taken beneath it.
  benthic: { id: 'benthic', targetingS: [0.2, 0.25], anticipationS: [0.1, 0.12], backBL: [0, 0], squash: [-0.04, -0.02], lungeS: [0.12, 0.16], reachBL: 1.2, nearBL: 0.1, closeShare: 0.1, captureS: [0.11, 0.13], recoveryS: [0.3, 0.35], overshootBL: [0, 0], followThrough: false, ease: easeOut(2), followBL: 0 },
};

/**
 * Each predator's way of striking (the brief's section 2): the roi from cover, the jacks in pursuit; the tiger shark's
 * for the developer's trigger alone (the simulation gives it no reef fish to eat, M251). The octopus's (`benthic`) is
 * ready and given to none: the simulation gives the octopus no fish to eat (its layer is posed since M252, but as the ulua's prey; review M267's: this said it could not be).
 */
export const PREDATOR_PROFILES: Readonly<Record<string, HuntProfileId>> = { roi: 'ambush', ulua: 'pursuit', omilu: 'pursuit', tigerShark: 'approach' };

/**
 * The share of a predator's strikes at a fish that catch it (M251), by its way of striking: the strikes that miss are
 * drawn beside the catches, as many as this gives for the day's catches. The one field count of a Hawaiian reef
 * predator's strikes found (M251's reader): the ʻōmilu's at Johnston Atoll, 173 strikes at spawning parrotfish and
 * surgeonfish in a reef channel over 77.5 hours of 1994 and 1995, "Number of attacks 149 / 24; Number of kills 3 / 4;
 * Attack success rate 2.0 % / 16.7 %", its midwater (pursuit) strikes and its ambush strikes (Sancho 1998, Ph.D.
 * thesis, MIT/WHOI 98-23, ch. 4, Table 1, p. 186; a field count, its facts cited). The jacks' pursuit is its midwater
 * strikes' (the ulua's own not counted there, "sighted during this study, but not observed attacking reef fishes",
 * p. 111: its congener's taken, JUDGED). The roi's ambush is its ambush strikes' (no grouper's count could be read:
 * Shpigel and Fishelson 1989 behind a host this environment refuses; JUDGED, the same way of striking measured on the
 * same reefs' prey). Its limit, the source's own: every strike counted was at prey spawning. The shark's and the
 * octopus's none: the simulation gives neither a reef fish to eat, so neither strikes but by the developer's trigger.
 */
export const CAPTURE_SUCCESS: Readonly<Record<HuntProfileId, number>> = { ambush: 4 / 24, pursuit: 3 / 149, approach: Number.NaN, benthic: Number.NaN };

/**
 * The hunts' limits (the brief's section 9, "an initial configurable mobile budget of one or two active hunts"): at most
 * `budget` strikes begun in any `gapS` (so at most `budget` at once, a strike lasting about a second; JUDGED: a day is five seconds at the game's own
 * speed, so the day's strikes in the hectare, about 4.5 with the jacks' misses at 2% (MEASURED: the mean day of 2026, seeds 1 and 42, every fifth day), would come about a second apart; two at most begun together, then a wait, the reef not a spectacle, the brief's "not a constant visual spectacle"), and only where the predator is within `withinM` of the camera and drawn `leastPx` long or more on the
 * screen (JUDGED: a strike too far or too small to see is not drawn); a stage tried `triesPerS` times a second at most
 * (JUDGED: a search of the fish in view, not every frame's).
 */
export const HUNT_SETTINGS = { budget: 2, gapS: 10, withinM: 25, leastPx: 14, triesPerS: 4 } as const;

/** The hunts' limits as the page sets them: `?hunts=N` the budget, 0 none (M251, the brief's "expose it as a setting"). */
export function huntBudget(search: string): number {
  const asked = new URLSearchParams(search).get('hunts');
  const n = asked === null ? Number.NaN : Number(asked);
  return Number.isInteger(n) && n >= 0 && n <= 8 ? n : HUNT_SETTINGS.budget;
}

/**
 * Where a sprite's mouth is (the brief's section 5), measured from its own pixels (M251): its art faces left, so its
 * mouth is its leftmost pixels, the middle of the opaque rows of its first columns (`MOUTH_COLUMNS` of its width); as
 * a share of its width from its middle toward its front (`x`) and of its height up from its foot (`y`). Each sprite is
 * cropped to its fish (no clear row or column on any side, MEASURED, M251's survey), so its first opaque column is its
 * snout. Kept for each sprite once (the brief's "Cached capture anchors").
 */
/** JUDGED: a snout's few columns, enough rows to find its middle, few enough not to reach the head's depth. */
const MOUTH_COLUMNS = 0.04;
const mouths = new WeakMap<Sprite, { x: number; y: number }>();
export function mouthOf(sprite: Sprite): { x: number; y: number } {
  const known = mouths.get(sprite);
  if (known) return known;
  const opaque = (row: string, x: number): boolean => (row[x] ?? '.') !== '.';
  let first = -1;
  for (let x = 0; x < sprite.width && first < 0; x++) if (sprite.rows.some((r) => opaque(r, x))) first = x;
  const band = Math.max(1, Math.round(MOUTH_COLUMNS * sprite.width));
  let [rows, sum] = [0, 0];
  for (let x = Math.max(0, first); x < Math.max(0, first) + band; x++) {
    sprite.rows.forEach((r, y) => {
      if (opaque(r, x)) [rows, sum] = [rows + 1, sum + y];
    });
  }
  const mouth = { x: 0.5 - (Math.max(0, first) + band / 2) / sprite.width, y: rows > 0 ? 1 - (sum / rows + 0.5) / sprite.height : 0.5 };
  mouths.set(sprite, mouth);
  return mouth;
}

/** A strike the day holds (`dayAttempts`): its predator and prey, the prey's group where the day's rate names one, and whether it catches. */
export interface HuntAttempt {
  predator: string;
  prey: string;
  group?: 'young' | 'initial' | 'terminal';
  success: boolean;
}

/**
 * A day's strikes (M251): for each predator and prey, the day's expected catch rounded by a draw of the day (its whole
 * part, and one more at its fraction's odds), so over many days as many as the simulation expects and never more; and
 * as many misses beside them as the predator's catch share gives (`CAPTURE_SUCCESS`), rounded the same way; in an
 * order of the day's own. The same day gives the same strikes.
 */
export function dayAttempts(epochDay: number, rates: readonly ReefHuntRate[]): HuntAttempt[] {
  const rounded = (x: number, a: number, salt: number): number => Math.floor(x) + (hash3(epochDay, a, salt) < x - Math.floor(x) ? 1 : 0);
  const order: { a: HuntAttempt; at: number }[] = [];
  rates.forEach((r, j) => {
    const profile = PREDATOR_PROFILES[r.predator];
    const share = profile ? CAPTURE_SUCCESS[profile] : Number.NaN;
    if (!(share > 0 && share <= 1)) return;
    const kills = rounded(r.perDay, j, 2_510);
    const misses = rounded((r.perDay * (1 - share)) / share, j, 2_511);
    for (let k = 0; k < kills + misses; k++) order.push({ a: { predator: r.predator, prey: r.prey, ...(r.group ? { group: r.group } : {}), success: k < kills }, at: hash3(epochDay, j * 64 + k, 2_512) });
  });
  return order.sort((x, y) => x.at - y.at).map((x) => x.a);
}

/** One fish of a hunt as the scene holds it: its name in its layer, where it is drawn, its size, the way it faces and its sprite's mouth. */
export interface HuntAnimal {
  id: string;
  species: string;
  at: THREE.Vector3;
  width: number;
  height: number;
  right: boolean;
  shown: number;
  sprite: Sprite;
  tlMm: number;
  /** Its kind among the schools' (`FishSchools`), below 0 for a visitor or an octopus (a developer's strike takes a school's fish as its prey). */
  kind?: number;
  /** A school's fish's group: its young, its adults in their first phase, or its terminal males (M252). */
  group?: 'young' | 'initial' | 'terminal';
}

/** What the hunts are told of the fish each frame, and how they pose them: the scene's own (`ReefScene`). */
export interface HuntWorld {
  fish(): readonly HuntAnimal[];
  pose(id: string, pose: FishPose | null): void;
  floorAt(x: number, z: number): number;
  /** Whether an animal of that name is still drawn at all, shown or thinned (M266: one gone from the drawing is no longer waited on); taken as yes where not given. */
  has?(id: string): boolean;
}

/** A hunt as the developer's overlay shows it (the brief's section 11). */
export interface HuntDebug {
  predator: string;
  prey: string;
  state: HuntState;
  mouth: readonly [number, number, number];
  destination: readonly [number, number, number];
  outcome: 'catch' | 'miss';
  durationS: number;
  elapsedS: number;
  staged: 'day' | 'developer';
}

interface Hunt {
  profile: HuntProfile;
  predator: HuntAnimal;
  prey: HuntAnimal;
  success: boolean;
  staged: 'day' | 'developer';
  state: HuntState;
  /** Seconds into its state, and into the hunt. */
  t: number;
  elapsed: number;
  spans: { follow: number; targeting: number; anticipation: number; lunge: number; capture: number; recovery: number; escape: number };
  /** Whether it began by turning to and following its prey (M258), and so is posed whole from its targeting on. */
  chased: boolean;
  /** The day's strike it draws, given back to the day's to draw again where its follow is given up (review M258's: a catch let go had never been drawn). */
  attempt: HuntAttempt | null;
  /** The scene's day it began on: a catch made after the day turns is not hidden through the next (review M267's: its fish stayed gone all that day). */
  day: number | null;
  /** The pose's blend as its way back began, and the seconds it followed (both M258's second review: the way back from a follow given up mid-turn, and the overlay's span). */
  backFrom: number;
  followed: number;
  /** Whether its follow was given up, no strike after it. */
  gaveUp: boolean;
  back: number;
  squash: number;
  overshoot: number;
  /** The way the prey darts, on the level, and how far, its lengths. */
  away: THREE.Vector3;
  dart: number;
  right: boolean;
  /** The predator's foot where it began, where it gathered, where the strike began and where it ends; the prey's where it darted from. */
  start: THREE.Vector3;
  gathered: THREE.Vector3;
  struck: THREE.Vector3;
  end: THREE.Vector3;
  preyFrom: THREE.Vector3;
  predatorPose: FishPose;
  preyPose: FishPose | null;
  /** Whether a frame of its prey's shrinking has been drawn. */
  shrunk: boolean;
}

const TMP = { a: new THREE.Vector3(), b: new THREE.Vector3(), c: new THREE.Vector3(), d: new THREE.Vector3() };
/** The prey's flash, seconds (the brief's 50 to 80 ms; JUDGED its middle). */
const FLASH_S = 0.065;
/**
 * The least time a caught prey shrinks and fades after its flash, s, and how far gone it is drawn on its first frame
 * of it, a share (JUDGED: two frames at 60 a second, and half gone, so its swallowing is seen on a slow phone too).
 */
const SHRINK_S = 0.035;
const HALF_GONE = 0.5;
/** How far into its strike, a share, a prey that gets away starts its dart (JUDGED: it sees the strike coming). */
const DART_AT = 0.55;
/** A prey's dart, seconds, and its ease back to its school after, seconds (JUDGED: a burst, then its own swimming). */
const DART_S = 0.3;
const SETTLE_S = 0.8;
/**
 * How far a caught prey is drawn into the predator's mouth, its prey's lengths, and behind the predator from the camera,
 * m, so it reads swallowed (the brief's "disappear behind the predator's sprite"), and how small it is drawn at the end
 * of its catch, a share (JUDGED, each: a short way in, enough to sort behind it, and gone before it is a speck).
 */
const SWALLOW_BL = 0.3;
const BEHIND_M = 0.05;
const SWALLOWED_SCALE = 0.3;
/** The least clearance over the floor along a strike's line, m (the brief's section 7: "Hunts should not pass unrealistically through ... rock formations"; JUDGED, a few centimeters over the floor as drawn). */
const CLEAR_M = 0.05;
/** How far above or below its mouth a prey may be struck at, the predator's heights (JUDGED: a strike along its way, not up a wall, the brief's "Depth consistency"). */
const ABOVE_HEIGHTS = 1.5;
/**
 * A missed prey's dart: how far off the line from the predator it may turn, radians, either way (0.45, JUDGED: away, not
 * straight away), how far, its own lengths (2 to 3, JUDGED: a burst, not a flight), the predator's pause after its miss,
 * s (0.1 to 0.15, the brief's "A short pause by the predator before resuming movement"; JUDGED), and how quickly the
 * prey is taken from its swimming into its dart, s (JUDGED: two frames at 30 a second, a start, not a jump).
 */
const DART_TURN = 0.45;
const DART_BL = [2, 3] as const;
const PAUSE_S = [0.1, 0.15] as const;
const DART_IN_S = 0.06;
/**
 * A pursuer's following of its prey (M258), each JUDGED to read on screen as a turn and a short chase: how long it
 * follows, s (1 to 2, the user's "for a little"); how quickly it turns, s, posed from its own swimming over it; how fast it
 * closes on its prey, its lengths a second (a cruise, well under its strike's); how far behind its prey's middle it keeps
 * its mouth as it follows, a share of its reach (closed on, yet its strike still a strike); and how long it eases back to its own
 * swimming after, s, having left its path (its own recovery the least).
 */
const FOLLOW_S = [1, 2] as const;
const TURN_S = 0.25;
const FOLLOW_BL_PER_S = 2;
const FOLLOW_AT_REACH = 0.3;
const CHASED_BACK_S = 1.2;
/** The predator's carry on past its catch or its miss as it slows, its lengths (JUDGED: the brief's "Continues moving slightly forward"). */
const CARRY_BL = 0.15;

/** The Reef's hunts: the day's strikes, the ones under way, and the fish they pose. */
export class ReefHunts {
  private attempts: HuntAttempt[] = [];
  private day: number | null = null;
  private ratesKey = '';
  private readonly active: Hunt[] = [];
  /** The fish caught today, hidden until the next day's counts are drawn. */
  private readonly eaten = new Set<string>();
  /**
   * The fish caught on a day gone by and still drawn (M266): the simulation names no fish that died, so the day's new
   * counts may keep the one eaten (the fish they lose is the drawing's choice, an eaten one first, `FishSchools`); kept
   * hidden until it is out of view, so none comes back where it was caught.
   */
  private readonly returning = new Set<string>();
  /** The animation clock, seconds, and when each strike of the last `gapS` began on it. */
  private clock = 0;
  private starts: number[] = [];
  private sinceTry = Infinity;
  private serial = 0;

  constructor(private readonly world: HuntWorld, private budget: number = HUNT_SETTINGS.budget) {}

  /** The most hunts at once, 0 for none (`huntBudget`). */
  setBudget(budget: number): void {
    this.budget = Math.max(0, Math.floor(budget));
  }

  /**
   * A day's data (`MauiNuiReefData.hunts`): a new day's strikes in place of the last's, unplayed ones let go, and the
   * fish caught the day before drawn again (the counts are the simulation's, its deaths in them).
   */
  setDay(epochDay: number, rates: readonly ReefHuntRate[]): void {
    // A new day, or a world laid on the scene in place of another on the same day (a game loaded, `replaceWorld`).
    // Told apart by the rates' own figures: the same world's day laid again (a tag's hand-over relays it) the same.
    const key = JSON.stringify(rates);
    if (epochDay === this.day && key === this.ratesKey) return;
    [this.day, this.ratesKey] = [epochDay, key];
    this.attempts = dayAttempts(epochDay, rates);
    // A follow under way draws the last day's strike: never given back to this one's (M258's second review).
    for (const h of this.active) h.attempt = null;
    // Each given back once out of view, a catch whose strike is still recovering too (review M267's: it was posed back at
    // once at the strike's end, in view).
    for (const id of this.eaten) this.returning.add(id);
    this.eaten.clear();
  }

  /**
   * A frame (`seconds` of the animation clock): each hunt under way moved on, and where there is room in the budget
   * and a strike of the day's to draw, one begun on a predator and prey in view; none begun when `calm` (a player who
   * asks for less motion, as the schools hold still).
   */
  update(camera: THREE.Camera, seconds: number, canvasPx: number, calm: boolean, visible: readonly [number, number, number, number] = [-1, 1, -1, 1]): void {
    const dt = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 0.25) : 0;
    this.clock += dt;
    this.starts = this.starts.filter((t) => this.clock - t < HUNT_SETTINGS.gapS);
    this.sinceTry += dt;
    const tries = !(calm || this.starts.length >= this.budget || this.attempts.length === 0 || this.sinceTry < 1 / HUNT_SETTINGS.triesPerS);
    // The fish read only where a hunt is under way, one may begin, or a fish eaten before today waits to be given back
    // (review M267's: every drawn fish copied each frame).
    if (this.active.length > 0 || tries || this.returning.size > 0) {
      const fish = this.world.fish();
      const byId = new Map(fish.map((f) => [f.id, f]));
      for (const h of [...this.active]) this.step(h, dt, camera, byId, visible);
      // One eaten before today given back once out of view, or let go once the drawing has none of that name.
      for (const id of this.returning) {
        const f = byId.get(id);
        // Let go with its pose (review 31's second round: a pose left on a name the drawing had dropped hid the male
        // later come back to the same post).
        if (this.world.has && !this.world.has(id)) {
          this.world.pose(id, null);
          this.returning.delete(id);
        }
        else if (f && !inView(camera, f.at, visible)) {
          this.world.pose(id, null);
          this.returning.delete(id);
        } else this.world.pose(id, { at: new THREE.Vector3(), blend: 0, alpha: 0 });
      }
      if (tries) this.tryStart(camera, canvasPx, visible, fish);
    }
    for (const id of this.eaten) if (!this.active.some((h) => h.prey.id === id) && (!this.world.has || this.world.has(id))) this.world.pose(id, { at: new THREE.Vector3(), blend: 0, alpha: 0 });
  }

  /** One of the day's strikes begun where a predator and its prey are in view. */
  private tryStart(camera: THREE.Camera, canvasPx: number, visible: readonly [number, number, number, number], fish: readonly HuntAnimal[]): void {
    this.sinceTry = 0;
    for (let k = 0; k < this.attempts.length; k++) {
      const a = this.attempts[k] as HuntAttempt;
      const pair = this.stage(camera, canvasPx, visible, fish, a.predator, a.prey, false, a.group);
      if (!pair) continue;
      this.attempts.splice(k, 1);
      this.begin(pair[0], pair[1], a.success, 'day', camera, pair[2], a);
      return;
    }
  }

  /**
   * The developer's trigger (the brief's section 11): a strike begun now on the nearest predator of `predator` in view
   * and a prey of `prey` (any fish where none is named) within its reach, caught or missed as asked; outside the day's
   * strikes, counting nothing. Whether one could be begun.
   */
  trigger(camera: THREE.Camera, canvasPx: number, predator: string, prey: string | null, success: boolean, fish: readonly HuntAnimal[] = this.world.fish()): boolean {
    const pair = this.stage(camera, canvasPx, [-1, 1, -1, 1], fish, predator, prey, true);
    if (!pair) return false;
    this.begin(pair[0], pair[1], success, 'developer', camera, pair[2]);
    return true;
  }

  /** The hunts under way, for the developer's overlay. */
  debug(): HuntDebug[] {
    return this.active.map((h) => {
      const mouth = this.mouthAt(h.predator, h.right, h.predatorPose.at, TMP.a, null);
      // A follow's span as long as it ran once it is over; one given up no strike's after it (M258's second review).
      const follow = h.state === 'follow' ? h.spans.follow : h.followed;
      const strike = h.gaveUp ? 0 : h.spans.targeting + h.spans.anticipation + h.spans.lunge + (h.success ? h.spans.capture : h.spans.escape);
      const total = follow + strike + h.spans.recovery;
      // On its way back from a follow, its own place.
      const to = h.chased && h.state === 'recovery' ? h.predator.at : h.end;
      return {
        predator: h.predator.species, prey: h.prey.species, state: h.state,
        mouth: [mouth.x, mouth.y, mouth.z], destination: [to.x, to.y, to.z],
        outcome: h.success ? 'catch' : 'miss', durationS: total, elapsedS: h.elapsed, staged: h.staged,
      };
    });
  }

  /** The animals a hunt holds now, predator and prey, and those caught today (M265: the reef's other interactions leave them to the hunts). */
  busy(): ReadonlySet<string> {
    const out = new Set(this.eaten);
    for (const h of this.active) out.add(h.predator.id).add(h.prey.id);
    return out;
  }

  /** The day's strikes still to draw, and the fish caught today, for the tests. */
  pending(): { attempts: readonly HuntAttempt[]; eaten: readonly string[]; active: number } {
    return { attempts: this.attempts, eaten: [...this.eaten], active: this.active.length };
  }

  /** Every fish given back to its own swimming (the scene closed or a world replaced). */
  dispose(): void {
    for (const h of this.active.splice(0)) {
      this.world.pose(h.predator.id, null);
      this.world.pose(h.prey.id, null);
    }
    for (const id of this.eaten) this.world.pose(id, null);
    this.eaten.clear();
    this.attempts = [];
  }

  /** The predator's mouth, its foot at `foot`, facing `right`: its sprite's mouth (`mouthOf`) on the camera's plane, as the sprite is drawn. */
  private mouthAt(a: HuntAnimal, right: boolean, foot: THREE.Vector3, out: THREE.Vector3, camera: THREE.Camera | null): THREE.Vector3 {
    const m = mouthOf(a.sprite);
    const [side, up] = axes(camera);
    return out.copy(foot).addScaledVector(side, (right ? 1 : -1) * m.x * a.width).addScaledVector(up, m.y * a.height);
  }

  /**
   * A predator and a prey in view for a strike: the nearest predator to the camera with a prey in its reach and a clear
   * line to it, or, for a pursuer, one it turns to and follows first (M258, the third: whether it follows); a prey ahead
   * in its reach taken before one it must follow; null where none.
   */
  private stage(camera: THREE.Camera, canvasPx: number, visible: readonly [number, number, number, number], fish: readonly HuntAnimal[], predator: string, preyOf: string | null, developer: boolean, group?: HuntAttempt['group']): [HuntAnimal, HuntAnimal, boolean] | null {
    const busy = new Set(this.active.flatMap((h) => [h.predator.id, h.prey.id]));
    const eye = camera.getWorldPosition(TMP.c);
    const pxAtUnit = ((camera as THREE.PerspectiveCamera).projectionMatrix?.elements[5] ?? 0) * (canvasPx / 2);
    const seen = (f: HuntAnimal): boolean => {
      // None hidden as caught (review 31: one eaten yesterday, kept hidden in view, was struck again, unseen).
      if (f.shown < 0.5 || busy.has(f.id) || this.eaten.has(f.id) || this.returning.has(f.id)) return false;
      const d = f.at.distanceTo(eye);
      if (d > HUNT_SETTINGS.withinM) return false;
      if (!inView(camera, f.at, visible, TMP.d)) return false;
      return pxAtUnit <= 0 || (f.width * pxAtUnit) / Math.max(d, 1e-3) >= HUNT_SETTINGS.leastPx;
    };
    const predators = fish.filter((f) => f.species === predator && seen(f)).sort((a, b) => a.at.distanceTo(eye) - b.at.distanceTo(eye));
    const prof = HUNT_PROFILES[PREDATOR_PROFILES[predator] ?? 'pursuit'];
    const prey = fish.filter((f) => (preyOf === null ? f.species !== predator && (f.kind ?? 0) >= 0 : f.species === preyOf) && seen(f) && (developer || group === undefined || f.group === group));
    for (const p of predators) {
      let best: HuntAnimal | null = null;
      let bestKey = Infinity;
      let bestChase = false;
      const right0 = p.right;
      for (const q of prey) {
        // Smaller than its predator (a fish takes what fits its mouth) and within its reach of its mouth, at a depth its strike can reach.
        if (q.tlMm >= p.tlMm || q.width >= p.width) continue;
        // Ahead of it, on the side it faces (M257, the user's report: "the predator will sometimes be going the opposite
        // direction and then suddenly turn around to attack the prey, even when its really far away"): a prey behind it had
        // been measured from its mouth as it faced and struck at after it turned round. A pursuer may turn to one behind
        // it and follow it first (M258), its mouth measured as it would face it.
        const ahead = screenX(q.at, camera) > screenX(p.at, camera) === right0;
        if (!ahead && prof.followBL <= 0) continue;
        const mouth = this.mouthAt(p, ahead ? right0 : !right0, p.at, TMP.a, camera);
        const target = TMP.b.copy(q.at).addScaledVector(axes(camera)[1], q.height / 2);
        // A followed prey past its mouth as it would face it, or its follow gives it up on its first frame (M258's second review).
        if (!ahead && screenX(target, camera) > screenX(mouth, camera) !== !right0) continue;
        const d = mouth.distanceTo(target);
        const chase = !ahead || d > prof.reachBL * p.width;
        if (d > (chase ? prof.followBL : prof.reachBL) * p.width || d < prof.nearBL * p.width || Math.abs(target.y - mouth.y) > p.height * ABOVE_HEIGHTS) continue;
        if (!this.clear(mouth, target)) continue;
        // One it need not follow first, then the nearest.
        const key = (chase ? 1e6 : 0) + d;
        if (key < bestKey) [best, bestKey, bestChase] = [q, key, chase];
      }
      if (best) return [p, best, bestChase];
    }
    return null;
  }

  /** Whether a line between two points clears the floor (sampled at six points along it, no costlier search). */
  private clear(a: THREE.Vector3, b: THREE.Vector3): boolean {
    for (let k = 0; k <= 5; k++) {
      const p = TMP.d.copy(a).lerp(b, k / 5);
      if (p.y < this.world.floorAt(p.x, p.z) + CLEAR_M) return false;
    }
    return true;
  }

  private begin(predator: HuntAnimal, prey: HuntAnimal, success: boolean, staged: 'day' | 'developer', camera: THREE.Camera, chased = false, attempt: HuntAttempt | null = null): void {
    const profile = HUNT_PROFILES[PREDATOR_PROFILES[predator.species] ?? 'pursuit'];
    const n = this.serial++;
    const pick = (range: readonly [number, number], salt: number): number => range[0] + (range[1] - range[0]) * hash3(n, salt, 2_513);
    const right = screenX(prey.at, camera) > screenX(predator.at, camera);
    const away = TMP.a.copy(prey.at).sub(predator.at).setY(0);
    if (away.lengthSq() < 1e-9) away.set(right ? 1 : -1, 0, 0);
    away.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), (2 * hash3(n, 7, 2_513) - 1) * DART_TURN);
    const h: Hunt = {
      profile, predator, prey, success, staged, state: chased ? 'follow' : 'targeting', t: 0, elapsed: 0, chased, attempt, day: this.day, backFrom: 1, followed: 0, gaveUp: false,
      spans: {
        follow: chased ? pick(FOLLOW_S, 12) : 0,
        targeting: pick(profile.targetingS, 1), anticipation: pick(profile.anticipationS, 2), lunge: pick(profile.lungeS, 3),
        capture: pick(profile.captureS, 4), recovery: Math.max(pick(profile.recoveryS, 5), chased ? CHASED_BACK_S : 0), escape: DART_S + pick(PAUSE_S, 6),
      },
      back: pick(profile.backBL, 8), squash: pick(profile.squash, 9), overshoot: pick(profile.overshootBL, 10),
      away: away.clone(), dart: pick(DART_BL, 11), right,
      start: predator.at.clone(), gathered: predator.at.clone(), struck: predator.at.clone(), end: predator.at.clone(), preyFrom: prey.at.clone(),
      predatorPose: { at: predator.at.clone(), blend: 0, right },
      preyPose: null,
      shrunk: false,
    };
    this.active.push(h);
    this.starts.push(this.clock);
    this.world.pose(predator.id, h.predatorPose);
  }

  /** A hunt moved on `dt` seconds, its fish read again where they are drawn now; ended where either is gone. */
  private step(h: Hunt, dt: number, camera: THREE.Camera, byId: ReadonlyMap<string, HuntAnimal>, visible: readonly [number, number, number, number]): void {
    const predator = byId.get(h.predator.id);
    const prey = byId.get(h.prey.id) ?? (h.state === 'capture' || h.state === 'recovery' ? h.prey : undefined);
    if (!predator || !prey) {
      // Gone while it followed: its strike given back, undrawn (M258's second review).
      if (h.state === 'follow' && h.attempt) this.attempts.push(h.attempt);
      return this.end(h);
    }
    [h.predator, h.prey] = [predator, prey];
    h.t += dt;
    h.elapsed += dt;
    const p = h.profile;
    const pose = h.predatorPose;
    const [side, up] = axes(camera);
    const preyMiddle = (out: THREE.Vector3): THREE.Vector3 => out.copy(prey.at).addScaledVector(up, prey.height / 2);
    // Where its foot is when its mouth is at a point.
    const footFor = (point: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 => {
      const m = mouthOf(predator.sprite);
      return out.copy(point).addScaledVector(side, -(h.right ? 1 : -1) * m.x * predator.width).addScaledVector(up, -m.y * predator.height);
    };
    const floored = (v: THREE.Vector3): THREE.Vector3 => v.setY(Math.max(v.y, this.world.floorAt(v.x, v.z)));
    // From its mouth where it is drawn: its own swimming's, or where it has followed its prey to (M258).
    const toward = TMP.c.copy(preyMiddle(TMP.a)).sub(this.mouthAt(predator, h.right, h.chased ? pose.at : predator.at, TMP.b, camera));
    const dir = toward.lengthSq() > 1e-12 ? toward.clone().normalize() : new THREE.Vector3(h.right ? 1 : -1, 0, 0);
    if (h.state === 'follow') {
      // Turned round to its prey and taken from its own swimming over `TURN_S`, then after it at a cruise, closing until its
      // mouth is `FOLLOW_AT_REACH` of its reach behind the prey's middle, never backing off (review M258's: a prey coming at
      // it had sent it back tail first); on into its targeting when it has followed its while and its strike could be
      // staged (in reach, ahead of it, a clear line, at its depth), or given up, back to its own swimming and the day's
      // strike given back to draw again, where it cannot, or where the prey has passed it or left the screen (M258).
      pose.blend = smooth(h.t / TURN_S);
      const mouth = this.mouthAt(predator, h.right, pose.at, TMP.b, camera);
      const target = preyMiddle(TMP.a);
      const ahead = screenX(target, camera) > screenX(mouth, camera) === h.right;
      // In the part of the screen the panels leave, as its stage was (M258's second review).
      const inSight = inView(camera, target, visible, TMP.d);
      const goal = footFor(TMP.d.copy(target).addScaledVector(dir, -FOLLOW_AT_REACH * p.reachBL * predator.width), new THREE.Vector3());
      h.end.copy(goal);
      const gap = goal.sub(pose.at);
      const most = FOLLOW_BL_PER_S * predator.width * dt;
      if (gap.length() > most) gap.setLength(most);
      // Only toward the way it faces.
      if (gap.dot(side) * (h.right ? 1 : -1) < 0) gap.set(0, 0, 0);
      floored(pose.at.add(gap));
      if (!ahead || !inSight) this.giveUp(h, pose);
      else if (h.t >= h.spans.follow) {
        h.followed = h.t;
        const d = mouth.distanceTo(target);
        if (d <= p.reachBL * predator.width && d >= p.nearBL * predator.width && Math.abs(target.y - mouth.y) <= predator.height * ABOVE_HEIGHTS && this.clear(mouth, target)) {
          h.start.copy(pose.at);
          this.next(h, 'targeting');
        } else this.giveUp(h, pose);
      }
    } else if (h.state === 'targeting') {
      // Turned to its prey and slowed, closing a little, taken from its own swimming smoothly (whole already where it followed).
      const k = h.t / Math.max(1e-6, h.spans.targeting);
      pose.blend = h.chased ? 1 : smooth(k);
      const close = p.closeShare * toward.length() * smooth(k);
      h.gathered.copy(h.start).addScaledVector(dir, close);
      pose.at.copy(h.gathered);
      if (k >= 1) this.next(h, h.spans.anticipation > 0 ? 'anticipation' : 'lunge');
    } else if (h.state === 'anticipation') {
      // Gathered: drawn back a little and narrowed, held.
      const k = Math.min(1, h.t / Math.max(1e-6, h.spans.anticipation));
      const s = Math.sin(Math.PI * Math.min(1, k * 1.5));
      pose.at.copy(h.gathered).addScaledVector(dir, -h.back * predator.width * smooth(k));
      pose.scaleX = 1 - h.squash * s;
      if (k >= 1) this.next(h, 'lunge');
    } else if (h.state === 'lunge') {
      if (h.t <= dt) h.struck.copy(pose.at);
      const k = Math.min(1, h.t / Math.max(1e-6, h.spans.lunge));
      pose.scaleX = 1;
      // To its prey's middle where it catches it (followed as it swims); where it misses, past where the prey was.
      if (h.success) footFor(preyMiddle(TMP.a), h.end);
      else if (h.t <= dt) footFor(preyMiddle(TMP.a), h.end).addScaledVector(dir, h.overshoot * predator.width);
      pose.at.copy(h.struck).lerp(h.end, p.ease(k));
      floored(pose.at);
      if (!h.success && k >= DART_AT && !h.preyPose) {
        h.preyFrom.copy(prey.at);
        h.preyPose = { at: prey.at.clone(), blend: 0, right: screenX(TMP.a.copy(prey.at).add(h.away), camera) > screenX(prey.at, camera) };
        this.world.pose(prey.id, h.preyPose);
      }
      if (!h.success && h.preyPose) this.dart(h, dt);
      if (k >= 1) this.next(h, h.success ? 'capture' : 'escape');
    } else if (h.state === 'capture') {
      // The prey at its mouth: flashed, drawn into it and behind it, shrinking and fading; the predator carried on where it follows through.
      const k = Math.min(1, h.t / Math.max(1e-6, h.spans.capture));
      if (!h.preyPose) {
        h.preyFrom.copy(prey.at);
        h.preyPose = { at: prey.at.clone(), blend: 1 };
        this.world.pose(prey.id, h.preyPose);
      }
      const forward = p.followThrough ? h.overshoot * predator.width * easeOut(2)(k) : 0;
      pose.at.copy(h.end).addScaledVector(dir, forward);
      const mouth = this.mouthAt(predator, h.right, pose.at, TMP.a, camera);
      const inside = TMP.b.copy(mouth).addScaledVector(side, -(h.right ? 1 : -1) * SWALLOW_BL * prey.width * k);
      const behind = TMP.d.copy(inside).sub(camera.getWorldPosition(new THREE.Vector3())).normalize().multiplyScalar(BEHIND_M);
      // Flashed for its first `FLASH_S`, and on its first frame however long that frame (a slow phone's), whole while
      // it flashes; then shrunk and faded over the rest of its catch (`SHRINK_S` at least), its middle kept at the mouth
      // (its foot under it by half its height as drawn), at least one frame of it drawn part gone (`HALF_GONE`): so it
      // reads swallowed at any frame rate, not blinked out (the third reviewer's: at 30 frames a second the roi's catch
      // had shown it whole, then nothing).
      const flashing = h.t - dt < FLASH_S;
      const shrink = Math.max(SHRINK_S, h.spans.capture - FLASH_S);
      const since = h.t - FLASH_S;
      const gone = flashing ? 0 : h.shrunk ? smooth(since / shrink) : Math.min(HALF_GONE, smooth(since / shrink));
      if (!flashing) h.shrunk = true;
      h.preyPose.scale = 1 - (1 - SWALLOWED_SCALE) * gone;
      h.preyPose.at.copy(inside).add(behind).addScaledVector(up, (-prey.height * h.preyPose.scale) / 2);
      h.preyPose.blend = 1;
      h.preyPose.flash = flashing ? 1 : 0;
      h.preyPose.alpha = 1 - gone;
      // Not caught before its flash, and a frame of its shrinking, are drawn.
      if (!flashing && gone >= 1) {
        // One begun before the day turned given back as M266's are, out of view (review M267's: hidden through the next day).
        if (h.day === this.day) this.eaten.add(prey.id);
        else this.returning.add(prey.id);
        this.next(h, 'recovery');
      }
    } else if (h.state === 'escape') {
      // Missed: carried on a little past, held a moment, as the prey gets away.
      this.dart(h, dt);
      if (h.t >= h.spans.escape) this.next(h, 'recovery');
    } else if (h.state === 'recovery') {
      // Slowed, carried a little on, and given back to its own swimming; one that followed its prey off its path turned to
      // its own place and eased back to it at its follow's pace, not slid back tail first (review M258's).
      if (h.t <= dt) {
        h.end.copy(pose.at);
        if (h.chased) {
          const back = predator.at.distanceTo(pose.at);
          h.spans.recovery = Math.max(h.spans.recovery, back / (FOLLOW_BL_PER_S * predator.width));
          // Toward its own place where it has left it, else its own facing (M258's second review: one given up mid-turn
          // had been snapped round to its prey).
          pose.right = back > 0.3 * predator.width ? screenX(predator.at, camera) > screenX(pose.at, camera) : predator.right;
        }
      }
      const k = Math.min(1, h.t / Math.max(1e-6, h.spans.recovery));
      pose.at.copy(h.end).addScaledVector(dir, h.chased ? 0 : CARRY_BL * predator.width * easeOut(2)(k));
      // From where its follow had taken it to (a follow given up mid-turn not jumped whole first).
      pose.blend = h.backFrom * (1 - smooth(k));
      if (!h.success && h.preyPose) this.dart(h, dt);
      if (k >= 1 && (h.success || !h.preyPose || h.preyPose.blend <= 0)) this.end(h);
    }
  }

  /** The prey that gets away: a burst of speed away from the strike, then eased back to its own swimming. */
  private dart(h: Hunt, dt: number): void {
    const pose = h.preyPose;
    if (!pose) return;
    const s = ((pose as FishPose & { t?: number }).t ?? 0) + dt;
    (pose as FishPose & { t?: number }).t = s;
    pose.at.copy(h.preyFrom).addScaledVector(h.away, h.dart * h.prey.width * easeOut(3)(s / DART_S));
    pose.at.setY(Math.max(pose.at.y, this.world.floorAt(pose.at.x, pose.at.z)));
    pose.blend = s < DART_IN_S ? s / DART_IN_S : s < DART_S ? 1 : Math.max(0, 1 - smooth((s - DART_S) / SETTLE_S));
  }

  /** A follow given up (M258): the predator back to its own swimming, the prey let go, the day's strike to draw again. */
  private giveUp(h: Hunt, pose: FishPose): void {
    h.end.copy(pose.at);
    h.backFrom = pose.blend;
    h.followed = h.t;
    h.gaveUp = true;
    if (h.attempt) this.attempts.push(h.attempt);
    h.attempt = null;
    this.next(h, 'recovery');
  }

  private next(h: Hunt, state: HuntState): void {
    h.state = state;
    h.t = 0;
  }

  private end(h: Hunt): void {
    const i = this.active.indexOf(h);
    if (i >= 0) this.active.splice(i, 1);
    h.state = 'idle';
    this.world.pose(h.predator.id, null);
    if (!this.eaten.has(h.prey.id) && !this.returning.has(h.prey.id)) this.world.pose(h.prey.id, null);
  }
}

/** A point's place across the screen, normalized device coordinates. */
function screenX(at: THREE.Vector3, camera: THREE.Camera): number {
  return TMP.d.copy(at).project(camera).x;
}

/** The camera's right and up, as a sprite facing it is laid (`FishBatch`'s quad on the view's plane); the world's x and y without one. */
function axes(camera: THREE.Camera | null): [THREE.Vector3, THREE.Vector3] {
  if (!camera) return [AXES[0].set(1, 0, 0), AXES[1].set(0, 1, 0)];
  const e = camera.matrixWorld.elements;
  return [AXES[0].set(e[0] as number, e[1] as number, e[2] as number).normalize(), AXES[1].set(e[4] as number, e[5] as number, e[6] as number).normalize()];
}
const AXES: [THREE.Vector3, THREE.Vector3] = [new THREE.Vector3(), new THREE.Vector3()];
